import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Building2, Plus, CheckCircle2, XCircle, Loader2,
  RefreshCw, AlertCircle, Pencil, ChevronRight, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const ALL_GRADES        = ["G1","G2","G3","G4","G5","G6","G7","G8"];
const ACADEMIC_SUBJECTS = ["Mathematics","English","Hindi","EVS","Science","Social Science","Sanskrit"];
const MODULE_SUBJECTS   = ["FM Module","FLN"];
const ALL_SUBJECTS      = [...ACADEMIC_SUBJECTS, ...MODULE_SUBJECTS];
const BOARD_OPTIONS     = ["NCERT","IGCSE","ICSE","State-GJ","State-MH","State-TN","Custom"];
const EMPTY_FORM        = { school_code:"", school_name:"", board:"", grade_range:[], subject_whitelist:[], status:"active" };

function authHdr(token) {
  return { Authorization:`Bearer ${token}`, "Content-Type":"application/json" };
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"});
}

export default function SchoolManager({ token, onUnauth }) {
  const [schools,     setSchools]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showCreate,  setShowCreate]  = useState(false);
  const [editTarget,  setEditTarget]  = useState(null); // school doc to edit

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/cr/schools`, { headers: authHdr(token) });
      if (res.status === 401) { onUnauth(); return; }
      setSchools(await res.json());
    } catch { toast.error("Failed to load schools"); }
    finally  { setLoading(false); }
  }, [token, onUnauth]);

  useEffect(() => { fetch_(); }, [fetch_]);

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-sky-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">School Manager</h1>
            <p className="text-sm text-slate-500">{schools.length} school{schools.length!==1?"s":""} registered</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetch_} disabled={loading} data-testid="school-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading?"animate-spin":""}`} /> Refresh
          </Button>
          <Button size="sm" onClick={()=>setShowCreate(true)} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="school-create-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New School
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
      ) : schools.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-2 border-slate-200">
          <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No schools yet</p>
          <p className="text-slate-400 text-sm mt-1">Click &quot;New School&quot; to register the first one.</p>
        </Card>
      ) : (
        <div className="rounded-xl border border-slate-200 overflow-x-auto bg-white shadow-sm">
          <table className="w-full text-sm" data-testid="schools-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-5 py-3 font-semibold text-slate-600">Code</th>
                <th className="text-left px-5 py-3 font-semibold text-slate-600">School Name</th>
                <th className="text-left px-5 py-3 font-semibold text-slate-600">Board</th>
                <th className="text-left px-5 py-3 font-semibold text-slate-600">Grades</th>
                <th className="text-left px-5 py-3 font-semibold text-slate-600">Subjects</th>
                <th className="text-left px-5 py-3 font-semibold text-slate-600">Status</th>
                <th className="text-left px-5 py-3 font-semibold text-slate-600">Created</th>
                <th className="text-left px-5 py-3 font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {schools.map((s,i) => (
                <tr key={s.school_code} className={`border-b border-slate-100 hover:bg-sky-50/30 transition-colors ${i%2===0?"":"bg-slate-50/40"}`} data-testid={`school-row-${s.school_code}`}>
                  <td className="px-5 py-3.5">
                    <span className="font-mono font-bold text-sky-700 bg-sky-50 px-2 py-0.5 rounded text-xs">{s.school_code}</span>
                  </td>
                  <td className="px-5 py-3.5 font-medium text-slate-800">{s.school_name}</td>
                  <td className="px-5 py-3.5">
                    {s.board
                      ? <Badge variant="secondary" className="text-xs px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 font-mono">{s.board}</Badge>
                      : <span className="text-slate-300 text-xs">—</span>
                    }
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex flex-wrap gap-1">
                      {(s.grade_range||[]).map(g=>(
                        <Badge key={g} variant="secondary" className="text-xs px-1.5 py-0 bg-violet-50 text-violet-700 border-violet-200">{g}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex flex-wrap gap-1">
                      {(s.subject_whitelist||[]).map(sub=>(
                        <Badge key={sub} variant="secondary" className="text-xs px-1.5 py-0 bg-emerald-50 text-emerald-700 border-emerald-200">{sub}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    {s.status==="active"
                      ? <span className="inline-flex items-center gap-1 text-emerald-700 font-medium text-xs"><CheckCircle2 className="w-3.5 h-3.5"/>Active</span>
                      : <span className="inline-flex items-center gap-1 text-slate-400 font-medium text-xs"><XCircle className="w-3.5 h-3.5"/>Inactive</span>
                    }
                  </td>
                  <td className="px-5 py-3.5 text-slate-500 text-xs">{fmtDate(s.created_at)}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-500 hover:text-sky-700" onClick={()=>setEditTarget(s)} data-testid={`school-edit-${s.school_code}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Link to={`/admin/schools/${s.school_code}`}>
                        <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs" data-testid={`school-open-${s.school_code}`}>
                          Open <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateSchoolModal open={showCreate} token={token} onClose={()=>setShowCreate(false)} onCreated={()=>{setShowCreate(false);fetch_();}} />
      {editTarget && (
        <EditSchoolModal
          school={editTarget}
          token={token}
          onClose={()=>setEditTarget(null)}
          onSaved={(updated)=>{setEditTarget(null);setSchools(s=>s.map(x=>x.school_code===updated.school_code?updated:x));}}
          onDeleted={(code)=>{setEditTarget(null);setSchools(s=>s.filter(x=>x.school_code!==code));}}
        />
      )}
    </div>
  );
}

// ── Create Modal ──────────────────────────────────────────────────────────────
function CreateSchoolModal({ open, token, onClose, onCreated }) {
  const [form, setForm]       = useState(EMPTY_FORM);
  const [saving, setSaving]   = useState(false);
  const [codeStatus, setCS]   = useState(null);

  useEffect(() => { if (open) { setForm(EMPTY_FORM); setCS(null); } }, [open]);

  useEffect(() => {
    const code = form.school_code.trim();
    if (code.length < 2) { setCS(null); return; }
    setCS("checking");
    const t = setTimeout(async () => {
      try {
        const res  = await fetch(`${API}/api/admin/cr/schools/check-code?code=${encodeURIComponent(code)}`,{headers:{Authorization:`Bearer ${token}`}});
        const data = await res.json();
        setCS(data.available?"available":"taken");
      } catch { setCS(null); }
    }, 500);
    return () => clearTimeout(t);
  }, [form.school_code, token]);

  const toggleGrade   = g => setForm(f=>({...f, grade_range:   f.grade_range.includes(g)   ?f.grade_range.filter(x=>x!==g)   :[...f.grade_range,g]}));
  const toggleSubject = s => setForm(f=>({...f, subject_whitelist: f.subject_whitelist.includes(s)?f.subject_whitelist.filter(x=>x!==s):[...f.subject_whitelist,s]}));

  const submit = async () => {
    if (!form.school_code.trim()) { toast.error("School code required"); return; }
    if (!form.school_name.trim()) { toast.error("School name required"); return; }
    if (!form.board)              { toast.error("Select a board"); return; }
    if (!form.grade_range.length) { toast.error("Select at least one grade"); return; }
    if (!form.subject_whitelist.length) { toast.error("Select at least one subject"); return; }
    if (codeStatus==="taken") { toast.error("Code already taken"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/cr/schools`,{method:"POST",headers:authHdr(token),body:JSON.stringify({...form,school_code:form.school_code.trim().toUpperCase(),school_name:form.school_name.trim()})});
      if (res.status===409) { toast.error("Code already taken"); setCS("taken"); return; }
      if (!res.ok) { const e=await res.json(); toast.error(e.detail||"Failed"); return; }
      toast.success("School created"); onCreated();
    } catch { toast.error("Network error"); }
    finally { setSaving(false); }
  };

  const codeIcon = ()=>{
    if (codeStatus==="checking") return <Loader2 className="w-4 h-4 animate-spin text-slate-400"/>;
    if (codeStatus==="available") return <CheckCircle2 className="w-4 h-4 text-emerald-500"/>;
    if (codeStatus==="taken")    return <XCircle className="w-4 h-4 text-red-500"/>;
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-school-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-sky-600"/>Register New School</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">School Code <span className="text-red-500">*</span></Label>
            <div className="relative">
              <input className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm font-mono uppercase tracking-wider focus:outline-none focus:ring-2 focus:ring-sky-500 pr-9" placeholder="e.g. SUNRISE" value={form.school_code} maxLength={20} onChange={e=>setForm({...form,school_code:e.target.value.toUpperCase()})} data-testid="school-code-input"/>
              <span className="absolute right-3 top-1/2 -translate-y-1/2">{codeIcon()}</span>
            </div>
            {codeStatus==="taken"     && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5"/>This code is already taken</p>}
            {codeStatus==="available" && <p className="text-emerald-600 text-xs mt-1">Code is available</p>}
            <p className="text-slate-400 text-xs mt-1">Student ID preview: <span className="font-mono">{form.school_code||"CODE"}-G5-001</span></p>
          </div>
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">School Name <span className="text-red-500">*</span></Label>
            <input className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" placeholder="e.g. Sunrise International School" value={form.school_name} onChange={e=>setForm({...form,school_name:e.target.value})} data-testid="school-name-input"/>
          </div>
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">Board <span className="text-red-500">*</span></Label>
            <select
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
              value={form.board}
              onChange={e=>setForm({...form,board:e.target.value})}
              data-testid="school-board-select"
            >
              <option value="">Select board…</option>
              {BOARD_OPTIONS.map(b=><option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-sm font-semibold mb-2 block">Grade Range <span className="text-red-500">*</span></Label>
            <div className="flex flex-wrap gap-2">
              {ALL_GRADES.map(g=>(
                <button key={g} type="button" onClick={()=>toggleGrade(g)} data-testid={`grade-toggle-${g}`} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${form.grade_range.includes(g)?"bg-violet-600 text-white border-violet-600":"bg-white text-slate-600 border-slate-200 hover:border-violet-300"}`}>{g}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm font-semibold mb-2 block">Subject Whitelist <span className="text-red-500">*</span></Label>
            <div className="grid grid-cols-2 gap-1.5">
              {ACADEMIC_SUBJECTS.map(s=>(
                <div key={s} className="flex items-center gap-2">
                  <Checkbox id={`cs-${s}`} checked={form.subject_whitelist.includes(s)} onCheckedChange={()=>toggleSubject(s)} data-testid={`subject-toggle-${s}`}/>
                  <Label htmlFor={`cs-${s}`} className="text-sm cursor-pointer font-normal">{s}</Label>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 my-2">
              <div className="flex-1 h-px bg-slate-100"/>
              <span className="text-xs text-slate-400 font-medium">Modules</span>
              <div className="flex-1 h-px bg-slate-100"/>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {MODULE_SUBJECTS.map(s=>(
                <div key={s} className="flex items-center gap-2">
                  <Checkbox id={`cs-${s}`} checked={form.subject_whitelist.includes(s)} onCheckedChange={()=>toggleSubject(s)} data-testid={`subject-toggle-create-${s}`}/>
                  <Label htmlFor={`cs-${s}`} className="text-sm cursor-pointer font-normal">{s}</Label>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm font-semibold mb-2 block">Status</Label>
            <div className="flex gap-3">
              {["active","inactive"].map(st=>(
                <button key={st} type="button" onClick={()=>setForm({...form,status:st})} data-testid={`status-toggle-${st}`} className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all capitalize ${form.status===st?(st==="active"?"bg-emerald-600 text-white border-emerald-600":"bg-slate-500 text-white border-slate-500"):"bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{st}</button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving||codeStatus==="taken"} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="school-submit-btn">
              {saving?<><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Creating...</>:<><Plus className="w-4 h-4 mr-1.5"/>Create School</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit Modal ────────────────────────────────────────────────────────────────
function EditSchoolModal({ school, token, onClose, onSaved, onDeleted }) {
  const [form, setForm] = useState({
    school_name:              school.school_name,
    board:                    school.board || "",
    grade_range:              [...school.grade_range],
    subject_whitelist:        [...school.subject_whitelist],
    status:                   school.status,
    grade_subject_grid_active: school.grade_subject_grid_active || false,
    grade_subject_grid:        school.grade_subject_grid || {},
  });
  const [saving,       setSaving]       = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting,     setDeleting]     = useState(false);
  const [confirmCode,  setConfirmCode]  = useState("");
  const [deleteImpact, setDeleteImpact] = useState(null); // {student_count, teacher_count} or null while loading
  const [loadingImpact, setLoadingImpact] = useState(false);

  // Sorted grades present in grade_range (G1→G8 order)
  const sortedGrades = [...form.grade_range].sort((a,b)=>parseInt(a.slice(1),10)-parseInt(b.slice(1),10));

  const toggleGrade   = g => setForm(f=>({...f,grade_range:       f.grade_range.includes(g)       ?f.grade_range.filter(x=>x!==g)       :[...f.grade_range,g]}));
  const toggleSubject = s => setForm(f=>({...f,subject_whitelist: f.subject_whitelist.includes(s)?f.subject_whitelist.filter(x=>x!==s):[...f.subject_whitelist,s]}));

  // Build default grid: all subjects checked for all grades
  const buildDefaultGrid = (grades, subjects) =>
    Object.fromEntries(grades.map(g => [g, [...subjects]]));

  const toggleGradeSubjectGrid = () => {
    if (!form.grade_subject_grid_active) {
      // Activating — auto-populate with all cells checked if not set yet
      const hasExistingConfig = Object.keys(form.grade_subject_grid).length > 0;
      const grid = hasExistingConfig ? form.grade_subject_grid : buildDefaultGrid(form.grade_range, form.subject_whitelist);
      setForm(f => ({ ...f, grade_subject_grid_active: true, grade_subject_grid: grid }));
    } else {
      setForm(f => ({ ...f, grade_subject_grid_active: false }));
    }
  };

  const toggleGridCell = (grade, subject) => {
    setForm(f => {
      const current = f.grade_subject_grid[grade] || [];
      const updated = current.includes(subject)
        ? current.filter(s => s !== subject)
        : [...current, subject];
      return { ...f, grade_subject_grid: { ...f.grade_subject_grid, [grade]: updated } };
    });
  };

  const submit = async () => {
    if (!form.school_name.trim()) { toast.error("School name required"); return; }
    if (!form.grade_range.length) { toast.error("Select at least one grade"); return; }
    if (!form.subject_whitelist.length) { toast.error("Select at least one subject"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/cr/schools/${school.school_code}`,{method:"PATCH",headers:authHdr(token),body:JSON.stringify(form)});
      if (!res.ok) { const e=await res.json(); toast.error(e.detail||"Save failed"); return; }
      const updated = await res.json();
      toast.success("School updated"); onSaved(updated);
    } catch { toast.error("Network error"); }
    finally { setSaving(false); }
  };

  const openDeleteConfirm = async () => {
    setShowDeleteConfirm(true);
    setLoadingImpact(true);
    setDeleteImpact(null);
    try {
      const res = await fetch(`${API}/api/admin/cr/schools/${school.school_code}/delete-impact`, { headers: authHdr(token) });
      if (!res.ok) { const e = await res.json(); toast.error(e.detail || "Failed to check school records"); setShowDeleteConfirm(false); return; }
      const data = await res.json();
      setDeleteImpact(data);
    } catch {
      toast.error("Network error");
      setShowDeleteConfirm(false);
    } finally {
      setLoadingImpact(false);
    }
  };

  const handleDelete = async () => {
    const hasRecords = (deleteImpact?.student_count || 0) + (deleteImpact?.teacher_count || 0) > 0;
    if (hasRecords && confirmCode !== school.school_code) { toast.error("School code does not match"); return; }
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/admin/cr/schools/${school.school_code}`, { method:"DELETE", headers:authHdr(token) });
      if (!res.ok) { const e=await res.json(); toast.error(e.detail||"Delete failed"); return; }
      const d = await res.json();
      const name = d.school_name || school.school_code;
      toast.success(
        (d.students_deleted || d.teachers_deleted)
          ? `${name} deleted — ${d.students_deleted} student${d.students_deleted===1?"":"s"} and ${d.teachers_deleted} teacher${d.teachers_deleted===1?"":"s"} removed`
          : `${name} deleted`
      );
      onDeleted(school.school_code);
    } catch { toast.error("Network error"); }
    finally { setDeleting(false); }
  };

  if (showDeleteConfirm) {
    const hasRecords = deleteImpact && ((deleteImpact.student_count || 0) + (deleteImpact.teacher_count || 0) > 0);
    return (
      <Dialog open={true} onOpenChange={()=>{setShowDeleteConfirm(false);setConfirmCode("");setDeleteImpact(null);}}>
        <DialogContent className="max-w-md" data-testid="delete-school-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5"/> Delete School — {school.school_code}
            </DialogTitle>
          </DialogHeader>

          {loadingImpact ? (
            <div className="py-8 flex items-center justify-center text-slate-400 text-sm" data-testid="delete-impact-loading">
              <Loader2 className="w-4 h-4 mr-2 animate-spin"/> Checking associated records…
            </div>
          ) : hasRecords ? (
            <div className="space-y-4 py-2" data-testid="delete-cascade-warning">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 space-y-1">
                <p className="font-semibold">
                  This school has {deleteImpact.student_count} student{deleteImpact.student_count===1?"":"s"} and {deleteImpact.teacher_count} teacher{deleteImpact.teacher_count===1?"":"s"}.
                </p>
                <p>Deleting this school will permanently remove all associated student and teacher accounts. This cannot be undone.</p>
              </div>
              <div>
                <Label className="text-sm font-semibold mb-1.5 block">
                  Type <span className="font-mono text-red-600">{school.school_code}</span> to confirm
                </Label>
                <input
                  className="w-full border border-red-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder={school.school_code}
                  value={confirmCode}
                  onChange={e=>setConfirmCode(e.target.value.toUpperCase())}
                  data-testid="delete-school-confirm-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={()=>{setShowDeleteConfirm(false);setConfirmCode("");setDeleteImpact(null);}}>Cancel</Button>
                <Button
                  onClick={handleDelete}
                  disabled={deleting || confirmCode !== school.school_code}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-delete-school-btn"
                >
                  {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Deleting...</> : <><Trash2 className="w-4 h-4 mr-1.5"/>Delete Permanently</>}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-2" data-testid="delete-simple-confirm">
              <p className="text-sm text-slate-600">
                Are you sure you want to delete <span className="font-mono font-semibold">{school.school_code}</span>? This school has no students or teachers. This cannot be undone.
              </p>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={()=>{setShowDeleteConfirm(false);setDeleteImpact(null);}}>Cancel</Button>
                <Button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-delete-school-btn"
                >
                  {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Deleting...</> : <><Trash2 className="w-4 h-4 mr-1.5"/>Delete</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-school-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-4 h-4 text-sky-600"/>
            Edit — <span className="font-mono text-sky-700">{school.school_code}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">School Name</Label>
            <input className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" value={form.school_name} onChange={e=>setForm({...form,school_name:e.target.value})} data-testid="edit-school-name-input"/>
          </div>
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">Board</Label>
            <select
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
              value={form.board}
              onChange={e=>setForm({...form,board:e.target.value})}
              data-testid="edit-school-board-select"
            >
              <option value="">Select board…</option>
              {BOARD_OPTIONS.map(b=><option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-sm font-semibold mb-2 block">Grade Range</Label>
            <div className="flex flex-wrap gap-2">
              {ALL_GRADES.map(g=>(
                <button key={g} type="button" onClick={()=>toggleGrade(g)} data-testid={`edit-grade-${g}`} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${form.grade_range.includes(g)?"bg-violet-600 text-white border-violet-600":"bg-white text-slate-600 border-slate-200 hover:border-violet-300"}`}>{g}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm font-semibold mb-2 block">Subject Whitelist</Label>
            <div className="grid grid-cols-2 gap-1.5">
              {ACADEMIC_SUBJECTS.map(s=>(
                <div key={s} className="flex items-center gap-2">
                  <Checkbox id={`es-${s}`} checked={form.subject_whitelist.includes(s)} onCheckedChange={()=>toggleSubject(s)} data-testid={`edit-subject-${s}`}/>
                  <Label htmlFor={`es-${s}`} className="text-sm cursor-pointer font-normal">{s}</Label>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 my-2">
              <div className="flex-1 h-px bg-slate-100"/>
              <span className="text-xs text-slate-400 font-medium">Modules</span>
              <div className="flex-1 h-px bg-slate-100"/>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {MODULE_SUBJECTS.map(s=>(
                <div key={s} className="flex items-center gap-2">
                  <Checkbox id={`es-${s}`} checked={form.subject_whitelist.includes(s)} onCheckedChange={()=>toggleSubject(s)} data-testid={`edit-subject-${s}`}/>
                  <Label htmlFor={`es-${s}`} className="text-sm cursor-pointer font-normal">{s}</Label>
                </div>
              ))}
            </div>
          </div>

          {/* ── Per-Grade Subject Customization ─────────────────────────────── */}
          <div className="border border-slate-200 rounded-xl p-3 bg-slate-50/50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-700">Customize by Grade</p>
                <p className="text-xs text-slate-400 mt-0.5">Override subject visibility per grade level</p>
              </div>
              <button
                type="button"
                onClick={toggleGradeSubjectGrid}
                data-testid="toggle-grade-subject-grid"
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${form.grade_subject_grid_active ? "bg-sky-600" : "bg-slate-200"}`}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${form.grade_subject_grid_active ? "translate-x-5" : "translate-x-0"}`}/>
              </button>
            </div>

            {form.grade_subject_grid_active && form.subject_whitelist.length > 0 && sortedGrades.length > 0 && (
              <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200 bg-white">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-3 py-2 text-slate-500 font-semibold sticky left-0 bg-slate-50 z-10 min-w-[110px]">Subject</th>
                      {sortedGrades.map(g=>(
                        <th key={g} className="px-3 py-2 text-center text-slate-500 font-semibold min-w-[52px]">{g}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {form.subject_whitelist.map((subject,ri)=>(
                      <tr key={subject} className={`border-b border-slate-100 ${ri%2===0?"":"bg-slate-50/40"}`}>
                        <td className="px-3 py-2 text-slate-700 font-medium sticky left-0 bg-inherit z-10 whitespace-nowrap">{subject}</td>
                        {sortedGrades.map(g=>{
                          const checked=(form.grade_subject_grid[g]||[]).includes(subject);
                          return (
                            <td key={g} className="px-3 py-2 text-center">
                              <Checkbox
                                checked={checked}
                                onCheckedChange={()=>toggleGridCell(g,subject)}
                                data-testid={`grid-cell-${g}-${subject.replace(/\s+/g,"-")}`}
                                className="mx-auto"
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div>
            <Label className="text-sm font-semibold mb-2 block">Status</Label>
            <div className="flex gap-3">
              {["active","inactive"].map(st=>(
                <button key={st} type="button" onClick={()=>setForm({...form,status:st})} data-testid={`edit-status-${st}`} className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all capitalize ${form.status===st?(st==="active"?"bg-emerald-600 text-white border-emerald-600":"bg-slate-500 text-white border-slate-500"):"bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{st}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between pt-2 border-t">
            <Button
              type="button"
              variant="ghost"
              className="text-red-500 hover:text-red-700 hover:bg-red-50 text-sm"
              onClick={openDeleteConfirm}
              data-testid="delete-school-btn"
            >
              <Trash2 className="w-4 h-4 mr-1.5"/> Delete School
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
              <Button onClick={submit} disabled={saving} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="edit-school-save-btn">
                {saving?<><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Saving...</>:"Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
